import moment from 'moment-timezone'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { timezoneSelector } from 'selectors/current_user'

import { MeetingContainer } from './meeting_container'
import {
  shouldAddTodayPlaceholder,
  hasFutureMeetings,
  addTodayPlaceholderIfNeeded
} from './utils'
import { useFilters } from '../../../hooks/meetings/filters'
import { useUpcomingPreset } from '../hooks/use_upcoming_preset'

const getFirstUpcomingEventKey = (meetings) => {
  if (!meetings || !Array.isArray(meetings)) return null
  for (const group of meetings) {
    const events = group.events || []
    for (const e of events) {
      if (!e.past && !e.placeholder) {
        return e.key ?? e.uuid ?? null
      }
    }
  }
  return null
}

export const MeetingList = ({
  meetings: meetingsData,
  retrieveNextPage,
  retrievePreviousPage,
  selectedMeetingId,
  hasNext,
  hasPrevious,
  showBuffers,
  isNextLoading,
  isPreviousLoading,
  contactUuid = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  onVisibleDateRangeChange,
  onScrollAnchorDateChange,
  scrollToDateTarget = null,
}) => {
  const { getFilterParams } = useFilters()
  const { presetPeriod } = useUpcomingPreset()
  const filterParams = getFilterParams()
  const timezone = useSelector(timezoneSelector)
  const todayStr = useMemo(() => moment().tz(timezone).format('YYYY-MM-DD'), [timezone])

  // Date-range presets (Today / This week / Last week) should not inject the legacy today
  // placeholder cards ("No upcoming meetings" + schedule CTA).
  const addTodayPlaceholder = useMemo(
    () => !presetPeriod && shouldAddTodayPlaceholder(filterParams, todayStr),
    [filterParams, todayStr, presetPeriod]
  )

  const hasFutureMeetingsValue = useMemo(() => hasFutureMeetings(meetingsData, todayStr), [meetingsData, todayStr])

  const meetings = useMemo(() => addTodayPlaceholderIfNeeded(
    meetingsData,
    todayStr,
    addTodayPlaceholder,
    hasFutureMeetingsValue
  ), [addTodayPlaceholder, hasFutureMeetingsValue, meetingsData, todayStr])

  const firstUpcomingEventKey = useMemo(() => getFirstUpcomingEventKey(meetings), [meetings])

  return (
    <MeetingContainer
      meetings={meetings}
      onLoadNext={retrieveNextPage}
      onLoadPrevious={retrievePreviousPage}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      isNextLoading={isNextLoading}
      isPreviousLoading={isPreviousLoading}
      selectedMeetingId={selectedMeetingId}
      showBuffers={showBuffers}
      contactUuid={contactUuid}
      onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
      onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
      firstUpcomingEventKey={firstUpcomingEventKey}
      onVisibleDateRangeChange={onVisibleDateRangeChange}
      onScrollAnchorDateChange={onScrollAnchorDateChange}
      scrollToDateTarget={scrollToDateTarget}
    />
  )
}
